/**
 * Payment providers, colour-coded — support scans these screens fast, and a
 * FastSpring row should be distinguishable from a Stripe one at a glance rather
 * than by reading the word.
 *
 * Each provider's own brand hue, carried by the monogram and the tint. The
 * label itself is `text-foreground`, so it stays readable on any background and
 * in both themes without a contrast check per colour.
 */
import { cn } from "@/lib/utils";

type Style = { label: string; color: string; mark: string };

const STYLES: Record<string, Style> = {
  fastspring: { label: "FastSpring", color: "#f5a623", mark: "FS" },
  stripe: { label: "Stripe", color: "#635bff", mark: "S" },
  bluesnap: { label: "BlueSnap", color: "#0aa3dc", mark: "BS" },
  apple: { label: "App Store", color: "#555555", mark: "" },
  google: { label: "Google Play", color: "#34a853", mark: "▶" },
  paypal: { label: "PayPal", color: "#003087", mark: "PP" },
};

const UNKNOWN: Style = { label: "Unknown", color: "#9ca3af", mark: "?" };

/**
 * Upstream writes provider names freely ("FastSpring", "fastspring",
 * "Stripe Inc"), so match on a normalised substring rather than equality — an
 * unrecognised value must still render, in neutral grey.
 */
function providerStyle(raw: string | null | undefined): Style {
  if (!raw) return UNKNOWN;
  const key = raw.toLowerCase().replace(/[^a-z]/g, "");
  for (const [id, style] of Object.entries(STYLES)) {
    if (key.includes(id)) return style;
  }
  return { ...UNKNOWN, label: raw };
}

export function ProviderBadge({
  provider,
  className,
  showLabel = true,
}: {
  provider: string | null | undefined;
  className?: string;
  showLabel?: boolean;
}) {
  const style = providerStyle(provider);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium text-foreground",
        className
      )}
      style={{ backgroundColor: `${style.color}22`, borderColor: `${style.color}80` }}
      title={style.label}
    >
      <span
        aria-hidden
        className="grid h-4 w-4 shrink-0 place-items-center rounded-full text-[9px] font-bold leading-none text-white"
        style={{ backgroundColor: style.color }}
      >
        {style.mark}
      </span>
      {showLabel && style.label}
    </span>
  );
}
